import { useCallback, useEffect } from 'react';
import useObjectState from './use-object-state';

const useForm = (initialValues = {}, opt = {}, inputs = []) => {
  const onSubmit = opt.onSubmit || (() => {});
  const onValidate = opt.onValidate || (() => ({}));
  const onChange = opt.onChange || (_ => _);
  const onBlur = opt.onBlur || (_ => _);
  const isAspectRatioLocked = opt.isAspectRatioLocked;
  const resetValues = opt.resetValues;
  const [state, setState] = useObjectState(
    () => ({
      values: initialValues,
      changed: {},
      touched: {},
      errors: onValidate(initialValues, {}),
    }),
    inputs,
  );

  const handleSubmit = useCallback(
    event => {
      if (event) event.preventDefault();
      setState(({ values, errors }) => ({
        values: values,
        changed: {},
        touched: {},
        errors: errors,
      }));
      onSubmit(state.values, state);
    },
    [state, onSubmit, setState],
  );

  const handleChange = useCallback(
    event => {
      const { name, value } = event.target;
      setState(({ values, changed }) => {
        const newValues = onChange({ ...values, [name]: value }, values, name);
        return {
          values: newValues,
          changed: { ...changed, [name]: true },
          touched: { [name]: true },
          errors: onValidate(newValues, values, name),
        };
      });
    },
    [onChange, onValidate, setState],
  );

  const handleBlur = useCallback(
    event => {
      const { name, value } = event.target;
      event.target.value = parseFloat(value);
      setState(prevState => {
        const { values, changed, touched } = prevState;
        let nextValues = { ...values };
        const { width: widthError, height: heightError } = onValidate(nextValues, {});
        nextValues[name] = onBlur(values, touched);
        return touched[name] && (widthError || heightError) && isAspectRatioLocked
          ? {
              values: nextValues,
              changed: changed,
              touched: touched,
              errors: onValidate(nextValues, {}),
            }
          : prevState;
      });
    },
    [setState, onValidate, onBlur, isAspectRatioLocked],
  );

  const initialValuesToState = useCallback(() => {
    (isAspectRatioLocked || resetValues) &&
      setState(() => ({
        values: initialValues,
        changed: {},
        touched: { aspect: true },
        errors: onValidate(initialValues, {}),
      }));
  }, [isAspectRatioLocked, initialValues, onValidate, setState]);

  useEffect(initialValuesToState, [isAspectRatioLocked, resetValues]);

  return {
    handleSubmit,
    handleChange,
    handleBlur,
    ...state,
  };
};
export default useForm;
